import React, { useState, useEffect } from 'react'
import cn from 'classnames'
import Webcam from 'react-webcam'
import { blobToDataURL, dataURLToBlob } from 'blob-util'
import { Button, Label } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import { isEmpty, isNil } from 'ramda'
import { getUserValue } from '../utils'
import styles from './style.css'

/**
 * @typedef WebcamProps
 * @type {object}
 * @property {function} getScreenshot
 */

/**
 * @typedef WebcamError
 * @type {object}
 * @property {string} name
 */

const VIDEO_CONSTRAINTS = {
  width: 350,
  height: 480
}

const MIN_TABLET_WIDTH = 768

export const SCREENSHOT_FORMAT = 'image/jpeg'

const ExternalFaceImage = ({ name = 'faceImage' }) => {
  const { t, i18n } = useTranslation()
  const [{ form, form: { accId }, user }, dispatch] = useStore()
  const [imageSrc, setImageSrc] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [error, setError] = useState({})
  const { isFaceMatch } = form[accId]
  const userValue = getUserValue(accId, user, name)
  let webcam = {}

  useEffect(() => {
    if (isFaceMatch) {
      dispatch({ type: 'addValidationFld', name })
    } else {
      dispatch({ type: 'removeFieldValue', name })
      dispatch({ type: 'removeValidationFld', name })
    }
  }, [isFaceMatch])

  useEffect(() => {
    const isGetUserMediaSupported =
      !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) ||
      !!navigator.getUserMedia ||
      !!navigator.webkitGetUserMedia ||
      !!navigator.mozGetUserMedia
    if (!isGetUserMediaSupported) {
      setErrorMessage(t('faceMatch.browserNoSupport'))
    }
  }, [i18n.language])

  useEffect(() => {
    if (!isEmpty(error)) {
      onError(error)
    }
  }, [i18n.language])

  useEffect(() => {
    if (!isNil(userValue)) {
      blobToDataURL(userValue).then(src => setImageSrc(src))
    }
  }, [userValue, name])

  const isMobile = () => {
    const isIphoneIpod = /iPhone|iPod/.test(window.navigator.userAgent)
    return isIphoneIpod || (!isIphoneIpod && window.innerWidth <= MIN_TABLET_WIDTH)
  }

  const createPhotoBlock = (isShown) => {
    let props = {
      audio: false,
      ref: setWebcam,
      screenshotFormat: SCREENSHOT_FORMAT,
      screenshotWidth: VIDEO_CONSTRAINTS.width * 2.5,
      screenshotQuality: 1,
      onUserMediaError: onError,
      facingMode: 'user'
    }
    props = isMobile()
      ? props
      : {
        ...props,
        width: VIDEO_CONSTRAINTS.width,
        videoConstraints: VIDEO_CONSTRAINTS
      }
    return (
      <div className={cn({ [styles.hidden]: !isShown })}>
        <Webcam {...props} />
        <Button onClick={handleCapturePhoto} className={styles.secondaryButton}>
          {t('faceMatch.capturePhoto')}
        </Button>
      </div>
    )
  }

  const showPhotoBlock = imageSrc => {
    const width = isMobile() ? {} : { width: VIDEO_CONSTRAINTS.width }
    return (
      <>
        <img src={imageSrc} className={styles.photo} style={width} />
        <Button onClick={handleRemovePhoto} className={styles.secondaryButton}>
          {t('faceMatch.removePhoto')}
        </Button>
      </>
    )
  }
  /**
   * @param {?WebcamProps} curWebcam
   */
  const setWebcam = (curWebcam) => { webcam = curWebcam }

  const handleCapturePhoto = () => {
    if (webcam) {
      const imageSrc = webcam.getScreenshot()
      setImageSrc(imageSrc)
      dispatch({ type: 'setExtFieldValue', name, value: dataURLToBlob(imageSrc) })
      dispatch({ type: 'setValidationFld', name, value: true })
    }
  }

  const handleRemovePhoto = () => {
    setImageSrc('')
    dispatch({ type: 'setExtFieldValue', name, value: null })
    dispatch({ type: 'setValidationFld', name, value: false })
  }

  /**
   * @param {WebcamError} error
   */
  const onError = (error) => {
    let errorMessage = t('faceMatch.unknownError')

    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      errorMessage = t('faceMatch.noCamera')
    } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      errorMessage = t('faceMatch.cameraAlreadyInUse')
    } else if (
      error.name === 'OverconstrainedError' ||
      error.name === 'ConstraintNotSatisfiedError'
    ) {
      errorMessage = t('faceMatch.tryAnotherCamera')
    } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      errorMessage = t('faceMatch.permissionNotAllowed')
    }

    setErrorMessage(errorMessage)
    setError(error)
  }

  return (
    isFaceMatch ? (
      <div id='faceImageBlock'>
        <Label htmlFor='' value={t('faceMatch.title')} valid={!!imageSrc} />
        {!errorMessage && (
          <div className={styles.cameraBlock}>
            {imageSrc && showPhotoBlock(imageSrc)}
            {createPhotoBlock(!imageSrc)}
          </div>
        )}
        {errorMessage && <div className={styles.cameraBlock}>{errorMessage}</div>}
      </div>) : null
  )
}

const getExternalFaceImageField = field => {
  const { name, order } = field
  return (
    <ExternalFaceImage
      key={name}
      order={order}
      name={name}
    />
  )
}

export { ExternalFaceImage, getExternalFaceImageField }
